import { Editor } from '@components/index'
import { AccessControl } from '@components/layout'
import { ContractTable } from '@features/contracts/components/ContractTable'
import { RentTable } from '@features/contracts/components/RentTable'
import { AttachmentSection } from '@features/flats/components/AttachmentSection'
import { FlatsWidget } from '@features/flats/components/FlatsWidget'
import { useUpdateOffer } from '@services/offers/mutations/useUpdateOffer'
import {
  CreditCard,
  FileText,
  Home as HomeIcon,
  LayoutDashboard,
  type LucideIcon,
  Save,
  StickyNote
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import type { OfferDetail, OfferWrite } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import { OfferActions } from './OfferActions'
import { OfferDetailsCard } from './OfferDetailsCard'
import { OfferProtocols } from './OfferProtocols'

type Translate = (key: string) => string

// Payments/Documents only exist once the offer is reserved (has a contract).
const RESERVED_AND_LATER = ['RESERVED', 'ONGOING', 'NOTICE_PERIOD', 'TERMINATED']
const isActive = (status: string | undefined) =>
  !!status && RESERVED_AND_LATER.includes(status)

function OverviewTab({
  offer,
  id,
  description,
  setDescription,
  onSave,
  isSaving,
  t
}: {
  offer: OfferDetail | undefined
  id?: string
  description: string
  setDescription: (v: string) => void
  onSave: () => void
  isSaving: boolean
  t: Translate
}) {
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {offer?.status && id && (
          <OfferActions
            status={offer.status}
            id={id}
            hasInspection={offer.has_inspection}
          />
        )}
        <OfferDetailsCard offer={offer} id={id} />
      </div>

      <Card className="space-y-5 p-6">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <StickyNote className="h-5 w-5" />
          </span>
          <h3 className="m-0 font-display text-lg font-bold tracking-tight text-ink">
            {t('form.labels.description')}
          </h3>
        </div>
        <Editor
          key={offer?.id || 'new'}
          content={offer?.description || ''}
          onUpdate={setDescription}
        />
        <div className="flex justify-end">
          <Button onClick={onSave} disabled={isSaving || !description}>
            <Save className="h-4 w-4" />
            {t('admin.offerWidget.saveNotes')}
          </Button>
        </div>
      </Card>
    </div>
  )
}

function PaymentsTab({ id }: { id?: string }) {
  return <Card className="p-5">{id && <RentTable offerId={id} noSearch />}</Card>
}

function SectionCard({
  title,
  icon: Icon,
  children
}: {
  title: string
  icon?: LucideIcon
  children: React.ReactNode
}) {
  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-center gap-2.5">
        {Icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </span>
        )}
        <h3 className="m-0 font-display text-base font-bold tracking-tight text-ink">
          {title}
        </h3>
      </div>
      {children}
    </Card>
  )
}

function DocumentsTab({
  offer,
  id,
  t
}: {
  offer: OfferDetail | undefined
  id?: string
  t: Translate
}) {
  return (
    <div className="space-y-6">
      <SectionCard title={t('admin.offerWidget.contracts')} icon={FileText}>
        {id && <ContractTable offerId={id} noSearch />}
      </SectionCard>
      <OfferProtocols offerId={id} />
      <AttachmentSection
        attachments={
          offer?.attachments?.reduce((acc: any, attachment) => {
            if (attachment.category === 'OTHER') {
              acc.other = acc.other || []
              acc.other.push(attachment)
            }
            return acc
          }, {}) || null
        }
        title={t('admin.offerWidget.attachments')}
        contract_id={offer?.parent_contract}
        type="offer"
        category="OTHER"
      />
    </div>
  )
}

function FlatTab({ offer, t }: { offer: OfferDetail | undefined; t: Translate }) {
  return (
    <SectionCard title={t('admin.offerWidget.flat')} icon={HomeIcon}>
      <FlatsWidget flat={offer?.flat} offer={offer} />
    </SectionCard>
  )
}

export function OfferWidget(props: { offer: OfferDetail | undefined; id?: string }) {
  const { offer, id } = props
  const { t } = useTranslation('offers')
  const [searchParams, setSearchParams] = useSearchParams()
  const [description, setDescription] = useState(offer?.description || '')
  const { mutate: updateOffer, isPending: isSaving } = useUpdateOffer()

  useEffect(() => {
    if (offer?.description !== undefined) setDescription(offer.description || '')
  }, [offer?.description])

  const onSave = () => {
    if (!id) return
    // Partial update — backend PATCHes only the provided fields.
    updateOffer({ id, body: { description } as OfferWrite })
  }

  const showActiveTabs = isActive(offer?.status)

  const items: {
    key: string
    label: string
    icon: LucideIcon
    children: React.ReactNode
  }[] = [
    {
      key: 'overview',
      label: t('admin.tabs.overview'),
      icon: LayoutDashboard,
      children: (
        <OverviewTab
          offer={offer}
          id={id}
          description={description}
          setDescription={setDescription}
          onSave={onSave}
          isSaving={isSaving}
          t={t}
        />
      )
    },
    ...(showActiveTabs
      ? [
          {
            key: 'payments',
            label: t('admin.tabs.payments'),
            icon: CreditCard,
            children: <PaymentsTab id={id} />
          },
          {
            key: 'documents',
            label: t('admin.tabs.documents'),
            icon: FileText,
            children: <DocumentsTab offer={offer} id={id} t={t} />
          }
        ]
      : []),
    {
      key: 'flat',
      label: t('admin.tabs.flat'),
      icon: HomeIcon,
      children: <FlatTab offer={offer} t={t} />
    }
  ]

  const tabParam = searchParams.get('tab')
  const active = items.some((i) => i.key === tabParam)
    ? (tabParam as string)
    : 'overview'
  const setActive = (key: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', key)
      return next
    })

  return (
    <AccessControl role="ADMIN">
      <div className="mb-6 flex w-full gap-1 rounded-full border border-ink/[0.06] bg-ink/[0.03] p-1 sm:inline-flex sm:w-auto">
        {items.map((tab) => {
          const isTabActive = active === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActive(tab.key)}
              className={
                isTabActive
                  ? 'flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full border-0 bg-white px-3 py-2 text-sm font-semibold text-ink shadow-sm transition-colors sm:flex-initial sm:px-4'
                  : 'flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full border-0 bg-transparent px-3 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-ink sm:flex-initial sm:px-4'
              }
            >
              <tab.icon className="hidden h-4 w-4 sm:block" />
              {tab.label}
            </button>
          )
        })}
      </div>

      <div>{items.find((tab) => tab.key === active)?.children}</div>
    </AccessControl>
  )
}
